// Ported from SL Desktop's Vision code
import pMemoize, { pMemoizeClear } from 'p-memoize';
import type { Pid } from './runner';

interface Manifest {
  version: string;
  platform: string;
  url: string;
  size: number;
  checksum: string;
  timestamp: number;
}

interface UpdaterResponse {
  needsUpdate: boolean;
  installedManifest?: Manifest;
  latestManifest?: Manifest;
}

export type Path = string;
/**
 * The root directory where the Vision app was extracted to.
 */
export type VisionPath = Path | undefined;

export enum VisionEnv {
  Staging = 'staging',
  Production = 'production',
  Local = 'local',
}

// A clean way to track which native bindings we need.
// Makes Updater portable through simple DI.
export interface Deps {
  /**
   * Join two paths (usually a directory and a filename) together,
   * similar to NodeJS' `path.join`, except is non-variadic.
   *
   * It should use OS-specific path separators.
   */
  pathJoin: (pathA: Path, pathB: Path) => Promise<Path>;
  /**
   * Receives a list of directories that need to exist.
   * The implementation will have to create them if needed.
   */
  ensureDirs: (dirs: Path[]) => Promise<unknown>;
  readFileToString: (path: Path) => Promise<string>;
  parseJSON: <T = unknown>(jsonStr: string) => T;
  fetchJSON: <T = unknown>(url: URL) => Promise<T>;
  /**
   * Returns the app environment Vision is running as.
   * Honors the `$VISION_ENV` environment variable.
   */
  getVisionEnv: () => Promise<VisionEnv>;
  /**
   * Return a string that can be used as a cache buster param.
   *
   * @example
   * Math.floor(Date.now() / 1000).toString()
   */
  getCacheBusterString: () => string;
  spawnProcess: (path: Path, hidden?: boolean) => Promise<Pid>;
  terminateProcess: (pid: Pid) => Promise<unknown>;
}

export class VisionUpdater {
  constructor(private readonly deps: Deps) {}

  private checkCooldownMs = 60_000;

  resolvePaths = pMemoize(async (visionDir: NonNullable<VisionPath>) => {
    const {
      deps: { pathJoin },
    } = this;

    return {
      bin: await pathJoin(visionDir, 'bin'),
      manifest: await pathJoin(visionDir, 'manifest.json'),
      tmp: await pathJoin(visionDir, '.tmp'),
    };
  });

  private async ensureDirs(visionDir: VisionPath) {
    if (!visionDir) {
      return;
    }
    const paths = await this.resolvePaths(visionDir);
    await this.deps.ensureDirs([visionDir, paths.tmp]);
  }

  private async readInstalledManifest(
    visionDir: NonNullable<VisionPath>,
  ): Promise<Manifest | undefined> {
    const { readFileToString, parseJSON } = this.deps;
    try {
      const { manifest } = await this.resolvePaths(visionDir);
      return parseJSON(await readFileToString(manifest));
    } catch {
      return;
    }
  }

  private getEnvironment() {
    return this.deps.getVisionEnv();
  }

  private async getManifestUrl() {
    const cacheBuster = this.deps.getCacheBusterString();
    const env = await this.getEnvironment();
    // TODO: hardcoded platform and arch, update if backend ever supports more
    const url = new URL(
      env === VisionEnv.Staging
        ? '/staging/manifest_win_x86_64.json'
        : '/production/manifest_win_x86_64.json',
      'https://cdn-vision-builds.streamlabs.com',
    );
    url.searchParams.set('t', cacheBuster);
    return url;
  }

  private async forceUpdateCheck(visionDir: VisionPath) {
    pMemoizeClear(this.checkForUpdates);

    this.checkForUpdates(visionDir);
  }

  checkForUpdates = pMemoize(
    async (visionDir?: Path | undefined): Promise<UpdaterResponse> => {
      const installedManifest = visionDir
        ? await this.readInstalledManifest(visionDir)
        : undefined;
      const manifestUrl = await this.getManifestUrl();

      try {
        const latestManifest = await this.deps.fetchJSON<Manifest>(manifestUrl);

        const needsUpdate =
          !installedManifest ||
          latestManifest.version !== installedManifest.version ||
          latestManifest.timestamp > installedManifest.timestamp;

        return { needsUpdate, installedManifest, latestManifest };
      } catch {
        return { needsUpdate: false };
      }
    },
    {
      maxAge: this.checkCooldownMs,
    },
  );
}
